'use client';

import { GlobalAccessContext } from 'app/_providers/GlobalAccessProvider';

import * as Sentry from '@sentry/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { MemberAccessCheckRequestItem, MemberAccessCheckType } from 'shared/src/generated/openapi/v3/types';

export function usePermissions(accessChecks: MemberAccessCheckRequestItem[]): Record<string, boolean> {
  // Use useContext directly instead of useGlobalAccess to avoid throwing an error
  const globalAccessContext = useContext(GlobalAccessContext);
  const [loading, setLoading] = useState(false);

  // If the provider is not available, return empty permissions
  const getPermissions = globalAccessContext?.getPermissions || null;
  const requestPermissions = globalAccessContext?.requestPermissions || null;

  const cached = getPermissions ? getPermissions(accessChecks) : null;

  // Automatically request permissions if not cached
  useEffect(() => {
    if (requestPermissions && !cached && !loading) {
      setLoading(true);
      requestPermissions(accessChecks)
        .catch((error) => {
          Sentry.captureException(error, {
            level: 'warning',
            extra: {
              accessChecks,
              cached,
              loading,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [requestPermissions, cached, loading, accessChecks]);

  // Extract boolean values for each requested permission as an object
  const permissionValues = useMemo(() => {
    const permissions: Record<string, boolean> = {};

    accessChecks.forEach((check) => {
      if (!check.checks) {
        return;
      }

      check.checks.forEach((permission) => {
        let hasPermission = false;

        if (cached) {
          switch (check.accessType) {
            case MemberAccessCheckType.Organization:
              hasPermission = cached.organization?.[permission] || false;
              break;
            case MemberAccessCheckType.Workspace:
              // For workspace permissions, we need a workspaceId
              // For now, we'll need to enhance this based on workspace context
              if (check.workspaceId) {
                hasPermission = cached.workspace?.[permission] || false;
              } else {
                hasPermission = false;
              }
              // Workspace permission logic
              break;
            case MemberAccessCheckType.Product:
              // For product permissions, we need workspaceId and product
              if (check.workspaceId && check.products) {
                // For now, check the first product - this could be enhanced
                const productName = check.products[0]?.productName;

                if (productName) {
                  hasPermission = cached.product?.[permission] || false;
                } else {
                  hasPermission = false;
                }
              } else {
                hasPermission = false;
              }
              // Product permission logic
              break;
            case MemberAccessCheckType.System:
              // For system permissions, use the dedicated system permission method
              hasPermission = cached.system?.[permission] || false;
              break;
            default:
              hasPermission = false;
          }
        }

        permissions[permission] = hasPermission;
      });
    });

    return permissions;
  }, [cached, accessChecks, getPermissions]);

  return permissionValues;
}
